import { useMemo, useState, type ReactNode } from "react";
import {
  HomeOutlined,
  LeftOutlined,
  MenuOutlined,
  DownOutlined,
} from "@ant-design/icons";
import { useActiveCategories } from "../../../../hooks/useMenu";
import { useViewport } from "../../../../hooks/useViewport";
import { ONE_CHIP_ROW, useChipRows } from "./useChipRows";
import { ExpandRowsButton } from "./ExpandRowsButton";
import { CHIP_GRID, CHIP_NAV_CELL, ColorChip } from "./ColorChip";
import { HomeChip } from "./HomeChip";
import { buildGroupColorLookup } from "./catalogColors";
import { isLunchCategory } from "../../../../lib/lunchCatalog";
import { getTranslation } from "../../../../utils/translations";
import type { Category } from "../../../../api/menu";
import { useTranslation } from "react-i18next";

interface CategoryChipsProps {
  selectedCategoryId: number | null;
  onCategorySelect: (categoryId: number | null) => void;
  /** Свит (мобилен) вариант — родителят подава collapsed={!isDesktop} */
  collapsed?: boolean;
  /** Съдържание вдясно от свития селектор (напр. името на масата) —
      селекторът заема ~66%, trailing — ~33% от реда */
  trailing?: ReactNode;
}

// Икон-бутон от навигацията (къщичка / стрелка назад) в мобилния dropdown —
// 38×38; на десктоп къщичката е HomeChip, стрелката — клетка от мрежата
// (CHIP_NAV_CELL). Къщичката е РЕСЕТ (натиска се веднъж, връща „всички") —
// без активно състояние, същият вид като стрелката назад
const NAV_BTN_COMPACT =
  "w-[38px] h-[38px] rounded-[10px] border grid place-items-center shrink-0 cursor-pointer transition-colors bg-pos-panel border-pos-line text-pos-dim hover:border-pos-accent/40";

/**
 * Категорийна навигация на изглед „Продукти" — ЕДНА редица, ниво по ниво:
 * в корена се виждат главните категории; влезеш ли в категория с
 * подкатегории, главните се скриват и се виждат само нейните деца.
 * Къщичката връща в началото; стрелката назад — една стъпка нагоре по
 * дървото. Изборът е ФИЛТЪР (onCategorySelect), както досега.
 */
export const CategoryChips = ({
  selectedCategoryId,
  onCategorySelect,
  collapsed = false,
  trailing,
}: CategoryChipsProps) => {
  const { t, i18n } = useTranslation();
  const { isNarrow } = useViewport();
  const categories = useActiveCategories();
  // Отворен dropdown в свития (мобилен) вариант
  const [expanded, setExpanded] = useState(false);
  // Десктоп: разгънати ли са всички редове с chips (личната настройка
  // topFilterExpanded — ъгълчето „Всички" я записва директно) — логиката е
  // обща със StationChips (useChipRows).
  const { rowClipRef, rowOverflows, showAllRows, toggleShowAllRows } =
    useChipRows("topFilterExpanded", ONE_CHIP_ROW, [
      categories,
      selectedCategoryId,
      collapsed,
    ]);

  const catName = (cat: Category) =>
    getTranslation(cat.translations, i18n.language, cat.slug);
  // Цветът на групата (собственият category.color или този на прекия
  // родител, иначе неутрална) — кутийката на chip-а е в него (ColorChip)
  const groupColor = useMemo(
    () => buildGroupColorLookup(categories),
    [categories],
  );

  const childrenOf = useMemo(() => {
    // По sortIndex; категориите БЕЗ sortIndex — най-отзад
    return (parentId: number | null) =>
      categories
        .filter((cat) => cat.parentId === parentId)
        .sort(
          (a, b) =>
            (a.sortIndex ?? Number.MAX_SAFE_INTEGER) -
            (b.sortIndex ?? Number.MAX_SAFE_INTEGER),
        );
  }, [categories]);

  const selectedCat =
    categories.find((cat) => cat.id === selectedCategoryId) ?? null;

  // Родителят, чието ниво показваме: избраната категория, ако има деца;
  // иначе нейният родител (лист → стоим на нивото на листа). Null = корен.
  const viewParent = useMemo(() => {
    if (!selectedCat) return null;
    if (childrenOf(selectedCat.id).length > 0) return selectedCat;
    return categories.find((cat) => cat.id === selectedCat.parentId) ?? null;
  }, [selectedCat, categories, childrenOf]);

  const rowCats = childrenOf(viewParent?.id ?? null);

  // Клик на chip от реда: избор; повторен клик на активния — връща на родителя
  const handleChipClick = (cat: Category) => {
    onCategorySelect(
      cat.id === selectedCategoryId ? (viewParent?.id ?? null) : cat.id,
    );
  };

  // Стрелката назад: качва една стъпка нагоре по дървото
  const handleBackClick = () => {
    if (!viewParent) return;
    onCategorySelect(viewParent.parentId ?? null);
  };

  // Редът с chips (compact = мобилният dropdown). На десктоп всичко е в grid
  // с равни колони (CHIP_GRID) — къщичката („Всички") и стрелката са клетки
  // като категориите; в dropdown-а — flex-wrap по съдържанието с икон-бутони.
  const renderRow = (compact: boolean) => {
    return (
      <div
        className={
          compact ? "flex flex-wrap items-center gap-1 min-w-0" : CHIP_GRID
        }
      >
        {/* Къщичка — винаги връща към корена / всички категории */}
        {compact ? (
          <button
            type="button"
            title={t("ordering.allCategories")}
            onClick={() => {
              onCategorySelect(null);
              setExpanded(false);
            }}
            className={NAV_BTN_COMPACT}
          >
            <HomeOutlined className="text-base" />
          </button>
        ) : (
          <HomeChip
            title={t("ordering.allCategories")}
            onClick={() => onCategorySelect(null)}
          />
        )}

        {/* Стрелка назад — една стъпка нагоре по дървото (само в подниво) */}
        {viewParent && (
          <button
            type="button"
            title={t("common.back")}
            onClick={handleBackClick}
            className={compact ? NAV_BTN_COMPACT : CHIP_NAV_CELL}
          >
            <LeftOutlined className="text-sm" />
          </button>
        )}

        {compact && (
          <span aria-hidden className="w-px h-6 bg-pos-line shrink-0" />
        )}

        {/* Категориите от текущото ниво */}
        {rowCats.map((cat) => (
          <ColorChip
            key={cat.id}
            color={groupColor(cat.id)}
            active={cat.id === selectedCategoryId}
            compact={compact}
            hasChildren={childrenOf(cat.id).length > 0}
            lunch={isLunchCategory(cat)}
            onClick={() => {
              handleChipClick(cat);
              // В dropdown-а: листова категория затваря панела
              if (compact && childrenOf(cat.id).length === 0) {
                setExpanded(false);
              }
            }}
          >
            {catName(cat)}
          </ColorChip>
        ))}
      </div>
    );
  };

  // Свит (мобилен) вариант — селектор бутон + падащ панел със същия ред
  if (collapsed) {
    return (
      // Страничните отстъпи следват търсачката и списъка под реда
      // (ItemCatalog: px-3 на тесен екран, px-5 на таблет)
      <div className={`relative pt-2.5 ${isNarrow ? "px-3" : "px-5"}`}>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className={`h-10 rounded-xl border border-pos-ctl bg-pos-card px-3.5 flex items-center gap-2.5 cursor-pointer min-w-0 ${
              trailing ? "flex-[2]" : "w-full"
            }`}
          >
            <MenuOutlined className="text-pos-accent text-base" />
            <span className="text-sm font-bold text-pos-ink truncate">
              {selectedCat ? catName(selectedCat) : t("ordering.allCategories")}
            </span>
            <DownOutlined
              className={`ml-auto text-pos-dim text-xs transition-transform ${
                expanded ? "rotate-180" : ""
              }`}
            />
          </button>

          {/* Слот вдясно (име на маса) — ~33% от реда */}
          {trailing && <div className="flex-1 min-w-0">{trailing}</div>}
        </div>

        {expanded && (
          <>
            {/* Невидим backdrop — клик извън панела го затваря */}
            <div
              className="fixed inset-0 z-20"
              onClick={() => setExpanded(false)}
              aria-hidden
            />
            <div
              className={`dk-elevated absolute top-[48px] bg-pos-card border border-pos-ctl rounded-[14px] p-2.5 shadow-[0_20px_44px_rgba(60,40,20,.22)] z-30 max-h-[60vh] overflow-y-auto ${
                isNarrow ? "inset-x-3" : "inset-x-5"
              }`}
            >
              {renderRow(true)}
            </div>
          </>
        )}
      </div>
    );
  }

  // Разгънат (десктоп) вариант — бутонът „Всички" вдясно се вижда само
  // когато chips-овете прелеят на втори ред и ги показва/скрива
  return (
    <div className="px-5 py-3 border-b border-pos-line2 bg-pos-panel">
      <div className="flex items-start gap-3">
        <div
          ref={rowClipRef}
          className={`flex-1 min-w-0 ${
            showAllRows ? "" : "max-h-10 overflow-hidden"
          }`}
        >
          {renderRow(false)}
        </div>
        {rowOverflows && (
          <ExpandRowsButton
            expanded={showAllRows}
            onToggle={toggleShowAllRows}
          />
        )}
      </div>
    </div>
  );
};
